import { API_BASE_URL } from "src/config/api";
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  CardActions,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ConfirmationDialog from '../../../layouts/shared/ConfirmationDialog';

const cardStyles = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px',
  border: '1px solid #2196F3',
  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
};
const headerStyles = {
  fontSize: '1.3rem',
  fontWeight: 'bold',
  color: '#2196F3',
  textAlign: 'center',
};

const priceStyles = {
  fontSize: '1.2rem',
  color: '#E91E63',
};

const descriptionStyles = {
  textAlign: 'center',
  marginTop: '16px',
  color: '#777',
};

const listItemStyles = {
  padding: '0',
  marginBottom: '5px',
  textAlign: 'center',
};

const CustomizedPlanBox = (props) => {
  const [data, setData] = useState([]);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [itemIdToDelete, setItemIdToDelete] = useState(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = `${API_BASE_URL}/getCustomizedPlansByUserId?userId=${localStorage.getItem(
        'userId'
      )}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();

      if (responseData.success && responseData.data.length > 0) {
        setData(responseData.data);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBuyPlan = (plan, e) => {
    e.preventDefault();
    if (plan._id) {
      props.openPurchaseCustomizeForm(plan._id);
      const encodedSubscriptionId = encodeURIComponent(plan._id);
      const encodedSubscriptionName = 'Customize Plan';
      const encodedSubscriptionAmount = encodeURIComponent(plan.subscriptionAmount);
      navigate(
        `/dashboard/subscription/${encodedSubscriptionId}/${encodedSubscriptionName}/${encodedSubscriptionAmount}`
      );
    } else {
      toast(`Plan with ID ${plan._id} not getting response`);
    }
  };

  const handleDeletePlan = (plan, e) => {
    e.preventDefault();
    setItemIdToDelete(plan._id);
    setIsConfirmationDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsConfirmationDialogOpen(false);
    setIsLoading(true);
    try {
      const cleanToken = token.replace(/"/g, '');
      const config = {
        headers: {
          Authorization: `Bearer ${cleanToken}`,
        },
      };
      const response = await axios.post(
        `${API_BASE_URL}/deleteCustomPlanByUser`,
        { customPlanId: itemIdToDelete },
        config
      );
      if (response.data.success === true) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response.data.message);
      console.error('Error deleting address:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsConfirmationDialogOpen(false);
    setItemIdToDelete(null);
  };

  CustomizedPlanBox.propTypes = {
    openPurchaseCustomizeForm: PropTypes.func.isRequired,
  };

  return (
    <Card>
      <CardContent>
          <h2 style={{ fontSize: '2.2rem', fontWeight: '600', textAlign: 'center' }}>Customize Plan</h2>
          <Typography variant="h5" textAlign='center' color="gray" mb={8}>
            These plans are customized based on your IP and domain requirements.
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
            {isLoading ? (
              <CircularProgress />
            ) : (
              <>
                {data.length === 0 ? (
                  <Typography variant="body1" color='text.secondary' textAlign='center' mb={8}>
                    No Customize Plan
                  </Typography>
                ) : (
                  <Grid container spacing={4}>
                    {data.map((plan, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        <Card elevation={4} style={cardStyles}>
                          <CardContent>
                            <Typography variant="h4" style={headerStyles} gutterBottom>
                              Pricing Plan
                            </Typography>
                            <Typography variant="h4" style={priceStyles} align="center">
                              ${plan.subscriptionAmount || 0} for {plan.subscriptionDays || 0} Days
                            </Typography>
                            <Typography variant="body2" style={descriptionStyles} paragraph>
                              Created Date - {new Date(plan.createdAt).toLocaleDateString()}
                            </Typography>
                            <Divider style={{ margin: '16px 0' }} />
                            <List>
                              <ListItem style={listItemStyles}>
                                <ListItemText primary={`Subscription Days: ${plan.subscriptionDays}`} />
                              </ListItem>
                              <ListItem style={listItemStyles}>
                                <ListItemText primary={`Number of Nodes: ${plan.numberOfIp}`} />
                              </ListItem>
                              <ListItem style={listItemStyles}>
                                <ListItemText primary={`Number of Domains: ${plan.numberOfDomain}`} />
                              </ListItem>
                            </List>
                            {/* <Divider style={{ margin: '16px 0' }} /> */}
                          </CardContent>
                          <CardActions sx={{ justifyContent: 'center', gap: 1, width: '100%' }}>
                            <Button
                              variant="contained"
                              onClick={(e) => handleBuyPlan(plan, e)}
                              // sx={{ flex: 1 }}
                            >
                              Buy Plan
                            </Button>
                            <Button
                              variant="contained"
                              color="error"
                              onClick={(e) => handleDeletePlan(plan, e)}
                              // sx={{ flex: 1 }}
                            >
                              Delete Plan
                            </Button>
                          </CardActions>

                          <ConfirmationDialog
                            isOpen={isConfirmationDialogOpen}
                            onClose={handleCancelDelete}
                            onConfirm={handleConfirmDelete}
                            message="Are you sure you want to delete customized plan?"
                          />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </div>
      </CardContent>
    </Card>
  );
};

export default CustomizedPlanBox;
